import {
  BookmarkTypes,
  IBookmarkCompareCreatePayload,
  IBookmarkModelAndTypeMapping,
  IBookmarkTradeData,
  IBookmarkTradeDataCreatePayload,
  IBookmarkTradeDataRaw,
} from 'src/model/bookmark';
import { useQuery } from '@tanstack/react-query';
// import { TRADE_DATA_CACHE_TIME, TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME } from 'src/utils/constants/config';
import BookmarkService from 'src/services/api/bookmark';

export const QUERY_KEY_GET_BOOKMARK = 'QUERY_KEY_GET_BOOKMARK';
export const QUERY_KEY_GET_BOOKMARK_DETAIL = 'QUERY_KEY_GET_BOOKMARK_DETAIL';
export const QUERY_KEY_GET_BOOKMARK_DATA_UUID = 'QUERY_KEY_GET_BOOKMARK_DATA_UUID';

export function useQueryGetBookmark<T extends BookmarkTypes>(
  type: T,
  query: ExpectedAny,
): { data: IResponseList<IBookmarkModelAndTypeMapping[T]> | undefined; isFetching: boolean } {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_BOOKMARK, type, query],
    queryFn: () => BookmarkService.getBookmarks(type, query),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!type,
    placeholderData: (previousData) => previousData,
    // staleTime: TRADE_DATA_CACHE_TIME,
    // initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });
  let mappedData;
  // Looks complicated but it just remaps data to our structure and does some typescript types converting
  if (type === BookmarkTypes.TradeData) {
    if (data?.data) {
      mappedData = {
        data: (data.data as IBookmarkTradeDataRaw[]).map((rawTradeDataBookmark: IBookmarkTradeDataRaw) => {
          const mappedTradeDataBookmark = {
            ...rawTradeDataBookmark,
            ...rawTradeDataBookmark.trade_data_info,
            hs_code: rawTradeDataBookmark.trade_data_info?.data_uuid,
          } as ExpectedAny;
          delete mappedTradeDataBookmark.trade_data_info;
          return mappedTradeDataBookmark as IBookmarkTradeData;
        }),
      } as IResponseList<IBookmarkModelAndTypeMapping[T]>;
    }
  } else {
    mappedData = data as unknown as IResponseList<IBookmarkModelAndTypeMapping[T]>;
  }

  return {
    data: mappedData,
    isFetching,
  };
}

export function useQueryGetBookmarkByDataUuid<T extends BookmarkTypes>(
  type: T,
  dataUuid: string,
): { data: IResponse<IBookmarkModelAndTypeMapping[T]> | undefined; isFetching: boolean } {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_BOOKMARK_DETAIL, type, dataUuid],
    queryFn: () => BookmarkService.getBookmarkByDataUuid(type, dataUuid),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: Boolean(type) && Boolean(dataUuid),
    placeholderData: (previousData) => previousData,
  });
  let mappedData;
  // Looks complicated but it just remaps data to our structure and does some typescript types converting
  if (type === BookmarkTypes.TradeData) {
    if (data?.data) {
      const rawTradeDataBookmark = data?.data as IBookmarkTradeDataRaw;
      const test = {
        ...rawTradeDataBookmark,
        ...rawTradeDataBookmark.trade_data_info,
        hs_code: rawTradeDataBookmark.trade_data_info?.data_uuid,
      } as IBookmarkTradeData;
      mappedData = {
        data: test,
      } as IResponse<IBookmarkModelAndTypeMapping[T]>;
    }
  } else {
    mappedData = data as unknown as IResponse<IBookmarkModelAndTypeMapping[T]>;
  }

  return {
    data: mappedData,
    isFetching,
  };
}

export function useQueryGetDataUuid(body: IBookmarkTradeDataCreatePayload | IBookmarkCompareCreatePayload) {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEY_GET_BOOKMARK_DATA_UUID, JSON.stringify(body)],
    queryFn: () => BookmarkService.getBookmarkDataUuid(body),
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData,
    // staleTime: TRADE_DATA_CACHE_TIME,
    // initialDataUpdatedAt: TRADE_DATA_INITIAL_DATA_INVALIDATE_TIME,
  });

  return {
    data,
    isFetching,
  };
}
